import type { Group, MeshPhysicalMaterial, MeshStandardMaterial } from 'three';
import type { QualitySettings } from '../../../../hooks/use-three-kitchen/quality';
import type { ObjectMetadata } from '../../../types/engine.types';

/**
 * Configuration for ModelManager
 */
export interface ModelManagerConfig {
	/** Base path for model assets */
	basePath: string;
	/** Quality settings for shadows */
	qualitySettings: QualitySettings | null;
	/** Quality level (0-10) for conditional features */
	qualityLevel?: number;
	/** Materials map for material replacement */
	materialsMap?: Map<string, MeshStandardMaterial | MeshPhysicalMaterial>;
}

/**
 * Model load options
 */
export interface ModelLoadOptions {
	/** Model file path (relative to basePath) */
	path: string;
	/** Position */
	position?: { x: number; y: number; z: number };
	/** Rotation (in radians) */
	rotation?: { x: number; y: number; z: number };
	/** Scale */
	scale?: number | { x: number; y: number; z: number };
	/** Object metadata */
	metadata?: ObjectMetadata;
	/** Parent group to add to */
	parent?: Group;
	/** Enable shadows */
	castShadow?: boolean;
	receiveShadow?: boolean;
}

/**
 * Callbacks for model loading
 */
export interface ModelLoadCallbacks {
	onComplete?: () => void;
	onProgress?: (loaded: number, total: number) => void;
	onError?: (url: string) => void;
}

/**
 * Material alias mapping - maps Collada material names to our configured material keys
 */
export const MATERIAL_ALIAS_MAP: Record<string, string> = {
	'Steel_Br': 'Steel_Br-material',
	'Steel_Brushed': 'Steel_Br-material',
	'SteelBr': 'Steel_Br-material',
	'Steel': 'Steel_Br-material',
	'steel': 'Steel_Br-material',
	'__brushe': '__brushe-material',
	'4_brushe': '__brushe-material',
	'brushed': '__brushe-material',
	'Brushed': '__brushe-material',
	'_brushed': '__brushe-material',
	'handle': '__brushe-material',
	'Handle': '__brushe-material',
	'Metal_Al': 'Metal_Al-material',
	'Metal_Ro': 'Metal_Ro-material',
	'Metal_Ru': 'Metal_Ru-material',
	'Metal': 'Metal_Al-material',
	'metal': 'Metal_Al-material',
	'Copper': 'Copper-material',
	'copper': 'Copper-material',
	'Outer_Sh': 'Outer_Sh-material',
	'termomet': 'termomet-material',
	'Termomet': 'termomet-material',
	'thermometer': 'termomet-material',
	'Thermometer': 'termomet-material',
	'Thermome': 'termomet-material',
	'thermome': 'termomet-material',
	'thermo': 'termomet-material',
	'Thermo': 'termomet-material',
	'Thermometer2': 'Thermometer2-material',
	'LCD': 'LCD-material',
	'lcd': 'LCD-material',
	'display': 'LCD-material',
	'Display': 'LCD-material',
	'FlameSymbol': 'FlameSymbol-material',
	'FlameSym': 'FlameSymbol-material',
	'Flame': 'FlameSymbol-material',
	'flame': 'FlameSymbol-material',
	'LeGriddleLogo': 'LeGriddleLogo-material',
	'LeGriddl': 'LeGriddleLogo-material',
	'LeGriddle': 'LeGriddleLogo-material',
	'WoodTexture': 'WoodTexture-material',
	'WoodText': 'WoodTexture-material',
	'WOOD': 'WoodTexture-material',
	'Wood': 'WoodTexture-material',
	'wood': 'WoodTexture-material'
};

/**
 * Returns the set of appliance texture keys that are reachable via MATERIAL_ALIAS_MAP.
 * These are the unique values of MATERIAL_ALIAS_MAP — the only keys that
 * adjustModelMaterials can ever look up in the materials map.
 * Used to filter appliance texture loading to only what placed models can reference.
 */
export const getReachableApplianceTextureKeys = (): Set<string> =>
	new Set(Object.values(MATERIAL_ALIAS_MAP));

/**
 * Z-fighting offset
 */
export const Z_FIGHTING = 0.01625;
